import { useState, useCallback, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/redux/hooks';
import { setFocusedField } from '../../../store/redux/slices/template.slice';
import Cropper from 'react-easy-crop';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Button,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Box,
  Flex,
  Text,
  Divider,
  Input,
} from '@chakra-ui/react';
import getCroppedImg from './utils/getCroppedImg';
import { getImageDimensions } from './utils/imageDimensions';
import { withCropperCorsCacheBust } from './utils/withCropperCorsCacheBust';
import { ImageField } from '../../../store/types/template';
import { CustomInfoIcon } from '../../../theme/icons/CustomInfoIcon';
import { CustomUploadIcon } from '../../../theme/icons/CustomUploadIcon';
import BannerShapeWizard from './BannerShapeWizard';

interface CropModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageSrc: string;
  aspect: number;
  platform: 'shared' | 'apple' | 'google' | 'enhancedEventTicket';
  field: ImageField | 'artwork' | 'secondaryLogo' | 'venueMap';
  onCropComplete: (croppedBase64: string) => void;
  discardUnchanged?: boolean;
  expressivePassEnabled?: boolean;
}

const getFieldLabel = (field: string): string => {
  if (field === 'icon') return 'icon';
  if (field === 'banner' || field.startsWith('stamp')) return 'banner';
  if (field === 'logo' || field === 'wideLogo') return 'logo';
  if (field === 'thumbnail') return 'thumbnail';
  if (field === 'artwork') return 'artwork';
  if (field === 'secondaryLogo') return 'logo';
  if (field === 'venueMap') return 'venue map';
  return 'image';
};

const getInfoText = (isBanner: boolean): string =>
  isBanner
    ? 'Use a wide PNG for best results. Check the Apple and Google previews as banners can render differently on each'
    : 'Use a PNG with a transparent background for the best results';

const CHECKERED_BG = `repeating-conic-gradient(#999999 0% 25%, #b0b0b0 0% 50%) 0 0 / 80px 80px`;

const WIDE_ASPECT = 1032 / 336;
const SQUARE_ASPECT = 1032 / 812;
const MID_ASPECT = (WIDE_ASPECT + SQUARE_ASPECT) / 2;

const CropModal = ({
  isOpen,
  onClose,
  imageSrc,
  aspect,
  onCropComplete,
  platform,
  field,
  discardUnchanged = false,
  expressivePassEnabled,
}: CropModalProps) => {
  const dispatch = useAppDispatch();
  const templateType = useAppSelector((s) => s.template.templateType);
  const isBannerField = field === 'banner' || field.startsWith('stamp');

  const showRatioToggle =
    expressivePassEnabled &&
    platform === 'google' &&
    field === 'banner' &&
    templateType !== 'transit';

  const [workingImage, setWorkingImage] = useState(imageSrc);
  const [bannerRatio, setBannerRatio] = useState<'wide' | 'square'>('wide');
  const initialBannerRatioRef = useRef<'wide' | 'square' | null>(null);

  useEffect(() => {
    initialBannerRatioRef.current = null;
    if (!showRatioToggle || !isOpen) return;
    const img = document.createElement('img');
    img.onload = () => {
      const ratio = img.naturalWidth / img.naturalHeight;
      const detectedRatio = ratio < MID_ASPECT ? 'square' : 'wide';
      initialBannerRatioRef.current = detectedRatio;
      setBannerRatio(detectedRatio);
    };
    img.src = workingImage;
    return () => {
      img.onload = null;
    };
  }, [workingImage, showRatioToggle, isOpen]);

  const activeAspect = showRatioToggle
    ? bannerRatio === 'square'
      ? SQUARE_ASPECT
      : WIDE_ASPECT
    : aspect;

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const cropContainerRef = useRef<HTMLDivElement>(null);
  const [bannerCropSize, setBannerCropSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const dimensions = getImageDimensions(templateType, {
    expressivePassEnabled,
  });
  const idealSize =
    platform === 'enhancedEventTicket'
      ? dimensions.enhancedEventTicket?.[
          field as 'artwork' | 'secondaryLogo' | 'venueMap'
        ]
      : dimensions[platform]?.[field as ImageField];

  useEffect(() => {
    setWorkingImage(imageSrc);
  }, [imageSrc, isOpen]);

  useEffect(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  }, [workingImage]);

  const handleReplaceImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      if (base64) setWorkingImage(base64);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  useEffect(() => {
    setCrop({ x: 0, y: 0 });
    if (cropContainerRef.current && isBannerField && activeAspect !== 0) {
      const width = cropContainerRef.current.clientWidth;
      setBannerCropSize({ width, height: Math.round(width / activeAspect) });
    }
  }, [isBannerField, activeAspect]);

  const cropBoxRef = useCallback(
    (node: HTMLDivElement | null) => {
      (
        cropContainerRef as React.MutableRefObject<HTMLDivElement | null>
      ).current = node;
      if (!node || !isBannerField) return;
      const width = node.clientWidth;
      if (width > 0 && activeAspect !== 0) {
        setBannerCropSize({ width, height: Math.round(width / activeAspect) });
      }
    },
    [isBannerField, activeAspect]
  );

  const handleClose = useCallback(() => {
    dispatch(setFocusedField(null));
    onClose();
  }, [dispatch, onClose]);

  const onCropAreaChange = useCallback(() => {}, []);

  const cropperImage = withCropperCorsCacheBust(workingImage);
  const remoteMediaProps = /^https?:\/\//i.test(workingImage)
    ? { crossOrigin: 'anonymous' as const }
    : undefined;

  const handleSave = useCallback(async () => {
    try {
      const cropUnchanged =
        workingImage === imageSrc &&
        crop.x === 0 &&
        crop.y === 0 &&
        zoom === 1 &&
        (!showRatioToggle || bannerRatio === initialBannerRatioRef.current);
      if (discardUnchanged && cropUnchanged) {
        handleClose();
        return;
      }
      if (!cropContainerRef.current) return;
      const croppedImage = await getCroppedImg(
        cropperImage,
        cropContainerRef.current,
        platform,
        field,
        templateType,
        expressivePassEnabled
      );

      onCropComplete(croppedImage);
      onClose();
    } catch (e) {
      console.error(e);
    }
  }, [
    cropperImage,
    discardUnchanged,
    workingImage,
    imageSrc,
    crop.x,
    crop.y,
    zoom,
    showRatioToggle,
    bannerRatio,
    expressivePassEnabled,
    handleClose,
    platform,
    field,
    templateType,
    onCropComplete,
    onClose,
  ]);

  const fieldLabel = getFieldLabel(field);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <Input
          ref={replaceInputRef}
          type="file"
          accept=".png, .jpg, .jpeg"
          onChange={handleReplaceImage}
          display="none"
        />
        <Box px={6} pt={6} pb={0}>
          <Text
            fontSize="18px"
            fontFamily="Inter SemiBold, sans-serif"
            fontWeight="600"
          >
            Position your {fieldLabel}
          </Text>
          <Text fontSize="14px" color="grey3" mt={2}>
            Drag to reposition and use the slider or scroll to zoom
          </Text>
          <Divider mt={4} borderColor="lightGrey" />
        </Box>
        <ModalBody pt={4} position="relative">
          <Box
            ref={cropBoxRef}
            position="relative"
            width="100%"
            height="400px"
            borderRadius="8px"
            overflow="hidden"
            background={CHECKERED_BG}
            sx={{
              '.reactEasyCrop_Container': {
                cursor: 'grab',
              },
              '.reactEasyCrop_Container:active': {
                cursor: 'grabbing',
              },
              '.reactEasyCrop_CropArea': {
                boxShadow: '0 0 0 9999em rgba(0, 0, 0, 0.3) !important',
                border: '2px solid #c0c0c0 !important',
              },
            }}
          >
            {showRatioToggle && (
              <Flex
                position="absolute"
                bottom={3}
                left={3}
                zIndex={2}
                align="center"
                gap={2}
                bg="rgba(0, 0, 0, 0.6)"
                borderRadius="full"
                px={3}
                py={1.5}
                cursor="pointer"
                onClick={() =>
                  setBannerRatio((r) => (r === 'square' ? 'wide' : 'square'))
                }
                _hover={{ bg: 'rgba(0, 0, 0, 0.8)' }}
                userSelect="none"
              >
                <Box
                  as="svg"
                  width="16px"
                  height="16px"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  {bannerRatio === 'square' ? (
                    <path d="M2 6V2h4M14 10v4h-4M14 6V2h-4M2 10v4h4" />
                  ) : (
                    <path d="M1 8V4h4M15 8v4h-4M15 8V4h-4M1 8v4h4" />
                  )}
                </Box>
                <Text fontSize="12px" color="white" fontWeight="500">
                  {bannerRatio === 'square' ? 'Square (5:4)' : 'Wide (3:1)'}
                </Text>
              </Flex>
            )}
            {isOpen && (!isBannerField || bannerCropSize) && (
              <Cropper
                key={cropperImage}
                image={cropperImage}
                mediaProps={remoteMediaProps}
                crop={crop}
                zoom={zoom}
                aspect={activeAspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropAreaChange}
                showGrid={false}
                objectFit={isBannerField ? 'horizontal-cover' : 'contain'}
                cropSize={
                  isBannerField ? (bannerCropSize ?? undefined) : undefined
                }
                restrictPosition={false}
                minZoom={isBannerField ? 0.3 : 0.1}
                maxZoom={isBannerField ? 2 : 3}
              />
            )}
          </Box>
          <BannerShapeWizard visible={showRatioToggle} />

          <Flex align="center" mt={4} gap={3}>
            <Text fontSize="14px" fontWeight="500" flexShrink={0}>
              Zoom
            </Text>
            <Slider
              min={isBannerField ? 0.3 : 0.1}
              max={isBannerField ? 2 : 3}
              step={isBannerField ? 0.01 : 0.01}
              value={zoom}
              onChange={(val) => setZoom(val)}
            >
              <SliderTrack
                h="4px"
                borderRadius="full"
                bg="rgba(255, 73, 53, 0.2)"
              >
                <SliderFilledTrack bg="#FF4935" borderRadius="full" />
              </SliderTrack>
              <SliderThumb
                boxSize="12px"
                bg="white"
                border="1px solid #e0e0e0"
                boxShadow="0px 1px 3px rgba(0,0,0,0.15)"
              />
            </Slider>
            <Text
              fontSize="12px"
              color="grey3"
              flexShrink={0}
              minW="35px"
              textAlign="right"
            >
              {Math.round(zoom * 100)}%
            </Text>
          </Flex>

          <Flex align="center" mt={4} gap={3}>
            <Button
              variant="secondaryFlat"
              size="sm"
              leftIcon={<CustomUploadIcon boxSize="16px" color="#3A4A57" />}
              onClick={() => replaceInputRef.current?.click()}
            >
              Replace image
            </Button>
            {idealSize && (
              <Text fontSize="12px" color="grey">
                {`Recommended size: ${idealSize.width} × ${idealSize.height}px`}
              </Text>
            )}
          </Flex>

          <Flex
            align="center"
            mt={4}
            gap={3}
            bg="#EBF8FF"
            borderRadius="8px"
            px={4}
            py={3}
          >
            <CustomInfoIcon boxSize="20px" color="#3182CE" />
            <Text fontSize="13px" color="#2D3748">
              {getInfoText(isBannerField)}
            </Text>
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent="flex-end">
          <Flex align="center" gap={4}>
            <Text
              as="button"
              fontSize="14px"
              fontWeight="500"
              color="black"
              cursor="pointer"
              onClick={handleClose}
              _hover={{ opacity: 0.7 }}
            >
              Cancel
            </Text>
            <Button variant="gradient" size="sm" onClick={handleSave}>
              Apply
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CropModal;
